import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Json } from "@how/database";
import { buildObjectPath } from "@how/storage";
import {
  linkSchema,
  MAX_SERMON_AUDIO_BYTES,
  richTextSchema,
  SERMON_AUDIO_BUCKET,
  SERMON_AUDIO_TYPES,
  sermonVideoProvider,
  type RichText,
} from "@how/website-core";
import { storageFileName } from "./media-queries";

/*
 * Sermons and media (ADR 0061): the church's library, its series and speakers, and its podcast. Staff with
 * sermons.manage read the tables directly (the policies decide) and write through the definer functions of
 * 20261011130000_sermons_and_media.sql, which check sermons.manage and the sermons_media module again.
 */

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

const failed = (error: { message: string; code?: string } | null) => {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
};

// --------------------------------------------------------------------------------------------------- rows

export interface SeriesRow {
  readonly id: string;
  readonly title: string;
  readonly slug: string;
  readonly description: string | null;
  readonly artworkPath: string | null;
  readonly artworkAlt: string | null;
  readonly startsOn: string | null;
  readonly endsOn: string | null;
}

export interface SpeakerRow {
  readonly id: string;
  readonly name: string;
  readonly roleTitle: string | null;
  readonly personId: string | null;
  readonly isActive: boolean;
}

export interface ResourceLink {
  readonly label: string;
  readonly href: string;
}

export interface SermonRow {
  readonly id: string;
  readonly title: string;
  readonly slug: string;
  /** YYYY-MM-DD. */
  readonly preachedOn: string;
  readonly speakerId: string | null;
  readonly seriesId: string | null;
  readonly campusId: string | null;
  readonly scripture: readonly string[];
  readonly summary: string | null;
  readonly videoUrl: string | null;
  readonly audioPath: string | null;
  readonly audioUrl: string | null;
  readonly audioMime: string | null;
  readonly audioBytes: number | null;
  readonly audioDurationSeconds: number | null;
  readonly notes: RichText | null;
  readonly resources: readonly ResourceLink[];
  readonly artworkPath: string | null;
  readonly artworkAlt: string | null;
  readonly isPublished: boolean;
  readonly publishedAt: string | null;
}

export interface SermonSettingsRow {
  readonly enabled: boolean;
  readonly title: string;
  readonly description: string;
  readonly author: string;
  readonly email: string;
  readonly artworkPath: string | null;
  readonly category: string;
  readonly explicit: boolean;
}

export const PODCAST_CATEGORIES = [
  "Religion & Spirituality",
  "Christianity",
  "Religion",
  "Spirituality",
] as const;

// --------------------------------------------------------------------------------------------------- reading

export function useSermons() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["sermons", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<SermonRow[]> => {
      const { data, error } = await supabase
        .from("sermons")
        .select(
          "id, title, slug, preached_on, speaker_id, series_id, campus_id, scripture, summary, video_url, audio_path, audio_url, audio_mime, audio_bytes, audio_duration_seconds, notes, resources, artwork_path, artwork_alt, is_published, published_at",
        )
        // RLS admits every church the reader belongs to; the screen shows one church.
        .eq("tenant_id", tenantId ?? "")
        .order("preached_on", { ascending: false })
        .limit(1000);
      if (error) throw error;
      return data.map((row) => {
        const notes = richTextSchema.safeParse(row.notes);
        const resources = linkSchema.array().safeParse(row.resources);
        return {
          id: row.id,
          title: row.title,
          slug: row.slug,
          preachedOn: row.preached_on,
          speakerId: row.speaker_id,
          seriesId: row.series_id,
          campusId: row.campus_id,
          scripture: row.scripture,
          summary: row.summary,
          videoUrl: row.video_url,
          audioPath: row.audio_path,
          audioUrl: row.audio_url,
          audioMime: row.audio_mime,
          audioBytes: row.audio_bytes,
          audioDurationSeconds: row.audio_duration_seconds,
          notes: notes.success ? notes.data : null,
          resources: resources.success ? resources.data : [],
          artworkPath: row.artwork_path,
          artworkAlt: row.artwork_alt,
          isPublished: row.is_published,
          publishedAt: row.published_at,
        };
      });
    },
  });
}

export function useSermonSeries() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["sermon-series", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<SeriesRow[]> => {
      const { data, error } = await supabase
        .from("sermon_series")
        .select("id, title, slug, description, artwork_path, artwork_alt, starts_on, ends_on")
        .eq("tenant_id", tenantId ?? "")
        .order("starts_on", { ascending: false, nullsFirst: false })
        .order("title");
      if (error) throw error;
      return data.map((row) => ({
        id: row.id,
        title: row.title,
        slug: row.slug,
        description: row.description,
        artworkPath: row.artwork_path,
        artworkAlt: row.artwork_alt,
        startsOn: row.starts_on,
        endsOn: row.ends_on,
      }));
    },
  });
}

export function useSermonSpeakers() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["sermon-speakers", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<SpeakerRow[]> => {
      const { data, error } = await supabase
        .from("sermon_speakers")
        .select("id, name, role_title, person_id, is_active")
        .eq("tenant_id", tenantId ?? "")
        .order("name");
      if (error) throw error;
      return data.map((row) => ({
        id: row.id,
        name: row.name,
        roleTitle: row.role_title,
        personId: row.person_id,
        isActive: row.is_active,
      }));
    },
  });
}

export function useSermonSettings() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["sermon-settings", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<SermonSettingsRow> => {
      const { data, error } = await supabase
        .from("sermon_settings")
        .select(
          "podcast_enabled, podcast_title, podcast_description, podcast_author, podcast_email, podcast_artwork_path, podcast_category, podcast_explicit",
        )
        .eq("tenant_id", tenantId ?? "")
        .maybeSingle();
      if (error) throw error;
      return {
        enabled: data?.podcast_enabled ?? false,
        title: data?.podcast_title ?? "",
        description: data?.podcast_description ?? "",
        author: data?.podcast_author ?? "",
        email: data?.podcast_email ?? "",
        artworkPath: data?.podcast_artwork_path ?? null,
        category: data?.podcast_category ?? "Religion & Spirituality",
        explicit: data?.podcast_explicit ?? false,
      };
    },
  });
}

// --------------------------------------------------------------------------------------------------- checking

/** What a sermon's form holds before it is saved. */
export interface SermonDraft {
  readonly title: string;
  readonly preachedOn: string;
  /** A speaker from the list, by id, or a name typed for a new one. */
  readonly speakerId: string | null;
  readonly speakerName: string;
  readonly seriesId: string | null;
  readonly campusId: string | null;
  readonly scripture: readonly string[];
  readonly summary: string;
  readonly videoUrl: string;
  readonly audio:
    | { readonly kind: "none" }
    | { readonly kind: "link"; readonly url: string }
    | {
        readonly kind: "file";
        readonly path: string;
        readonly mime: string;
        readonly bytes: number;
        readonly durationSeconds: number | null;
      };
  readonly notes: RichText | null;
  readonly resources: readonly ResourceLink[];
  readonly artworkPath: string | null;
  readonly artworkAlt: string | null;
}

/** The problems with a sermon's form, by field, in the words the form uses; empty when it can be saved. */
export function sermonProblems(draft: SermonDraft): Partial<Record<string, string>> {
  const problems: Record<string, string> = {};
  if (draft.title.trim() === "") problems["title"] = "Give the sermon a title";
  else if (draft.title.trim().length > 160) problems["title"] = "At most 160 characters";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.preachedOn))
    problems["preachedOn"] = "Choose the date it was preached";
  if (draft.videoUrl.trim() !== "" && sermonVideoProvider(draft.videoUrl.trim()) === null) {
    problems["videoUrl"] =
      "Paste a YouTube or Vimeo video's address, such as https://www.youtube.com/watch?v=… or https://vimeo.com/123456789";
  }
  if (draft.audio.kind === "link" && !/^https:\/\/\S+$/.test(draft.audio.url.trim())) {
    problems["audioUrl"] = "An audio link starts with https://";
  }
  if (draft.scripture.length > 10) problems["scripture"] = "Up to ten references";
  if (draft.scripture.some((r) => r.trim().length > 80)) {
    problems["scripture"] = "Each reference is at most 80 characters";
  }
  if (draft.summary.length > 2000) problems["summary"] = "At most 2,000 characters";
  if (draft.resources.length > 20) problems["resources"] = "Up to twenty links";
  for (const link of draft.resources) {
    if (!linkSchema.safeParse(link).success) {
      problems["resources"] =
        "Each link needs a label and an address starting https://, / or mailto:";
      break;
    }
  }
  return problems;
}

/** Why an audio file cannot be uploaded, or null. */
export function audioFileProblem(file: {
  readonly type: string;
  readonly size: number;
}): string | null {
  if (!(SERMON_AUDIO_TYPES as readonly string[]).includes(file.type)) {
    return "That file is not MP3, AAC or M4A audio.";
  }
  if (file.size > MAX_SERMON_AUDIO_BYTES) {
    return "That file is larger than 50 MB. Export it at 64 to 96 kbps (an hour of speech is about 30 to 43 MB), or paste a link to where it is hosted.";
  }
  return null;
}

/** The JSON save_sermon reads. */
export function sermonFields(draft: SermonDraft): Record<string, Json> {
  const audio = draft.audio;
  return {
    title: draft.title.trim(),
    preachedOn: draft.preachedOn,
    speakerId: draft.speakerId ?? "",
    speakerName: draft.speakerId === null ? draft.speakerName.trim() : "",
    seriesId: draft.seriesId ?? "",
    campusId: draft.campusId ?? "",
    scripture: draft.scripture.map((r) => r.trim()).filter((r) => r !== ""),
    summary: draft.summary.trim(),
    videoUrl: draft.videoUrl.trim(),
    audioPath: audio.kind === "file" ? audio.path : "",
    audioUrl: audio.kind === "link" ? audio.url.trim() : "",
    audioMime: audio.kind === "file" ? audio.mime : "",
    audioBytes: audio.kind === "file" ? audio.bytes : null,
    audioDurationSeconds: audio.kind === "file" ? audio.durationSeconds : null,
    notes: draft.notes ?? [],
    resources: draft.resources.map((r) => ({ label: r.label.trim(), href: r.href.trim() })),
    artworkPath: draft.artworkPath ?? "",
    artworkAlt: draft.artworkAlt ?? "",
  };
}

// --------------------------------------------------------------------------------------------------- writing

function useSermonInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["sermons", tenantId] }),
      client.invalidateQueries({ queryKey: ["sermon-series", tenantId] }),
      client.invalidateQueries({ queryKey: ["sermon-speakers", tenantId] }),
      client.invalidateQueries({ queryKey: ["sermon-settings", tenantId] }),
    ]);
  };
}

export function useSaveSermon() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useSermonInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly sermonId: string | null;
      readonly draft: SermonDraft;
    }): Promise<string> => {
      if (tenantId === null) throw new Error("no church resolved");
      const { data, error } = await supabase.rpc("save_sermon", {
        p_tenant_id: tenantId,
        p_fields: sermonFields(input.draft),
        ...(input.sermonId === null ? {} : { p_sermon_id: input.sermonId }),
      });
      failed(error);
      return data as string;
    },
    onSuccess: invalidate,
  });
}

export function usePublishSermon() {
  const supabase = useSupabase();
  const invalidate = useSermonInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly sermonId: string; readonly published: boolean }) => {
      const { error } = await supabase.rpc("publish_sermon", {
        p_sermon_id: input.sermonId,
        p_published: input.published,
      });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

/** Deletes the sermon, then its uploaded audio file (an unreferenced file left behind is harmless). */
export function useDeleteSermon() {
  const supabase = useSupabase();
  const invalidate = useSermonInvalidation();
  return useMutation({
    mutationFn: async (sermonId: string) => {
      const { data, error } = await supabase.rpc("delete_sermon", { p_sermon_id: sermonId });
      failed(error);
      if (typeof data === "string" && data !== "") {
        await supabase.storage.from(SERMON_AUDIO_BUCKET).remove([data]);
      }
    },
    onSuccess: invalidate,
  });
}

/** The length of an audio file in whole seconds, read by the browser; null when it cannot tell. */
async function audioDuration(file: File): Promise<number | null> {
  if (typeof Audio === "undefined" || typeof URL.createObjectURL !== "function") return null;
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<number | null>((resolve) => {
      const audio = new Audio();
      audio.preload = "metadata";
      audio.onloadedmetadata = () => {
        resolve(
          Number.isFinite(audio.duration) && audio.duration > 0 ? Math.round(audio.duration) : null,
        );
      };
      audio.onerror = () => {
        resolve(null);
      };
      audio.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Uploads a sermon's audio to the church's area of the how-audio bucket (`<church>/sermons/<file>`). The storage
 * policies decide who may (sermons.manage); the bucket refuses anything that is not audio or is over 50 MB.
 */
export function useUploadSermonAudio() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: async (
      file: File,
    ): Promise<{ path: string; mime: string; bytes: number; durationSeconds: number | null }> => {
      if (tenantId === null) throw new Error("no church resolved");
      const problem = audioFileProblem(file);
      if (problem !== null) throw new Error(problem);
      const path = buildObjectPath(
        SERMON_AUDIO_BUCKET,
        tenantId,
        "sermons",
        storageFileName(file.name),
      );
      const durationSeconds = await audioDuration(file);
      const uploaded = await supabase.storage
        .from(SERMON_AUDIO_BUCKET)
        .upload(path, file, { contentType: file.type, upsert: false });
      if (uploaded.error) throw uploaded.error;
      return { path, mime: file.type, bytes: file.size, durationSeconds };
    },
  });
}

export function useSaveSeries() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useSermonInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly seriesId: string | null;
      readonly title: string;
      readonly description: string;
      readonly artworkPath: string | null;
      readonly artworkAlt: string | null;
      readonly startsOn: string | null;
      readonly endsOn: string | null;
    }): Promise<string> => {
      if (tenantId === null) throw new Error("no church resolved");
      const { data, error } = await supabase.rpc("save_sermon_series", {
        p_tenant_id: tenantId,
        p_title: input.title.trim(),
        ...(input.seriesId === null ? {} : { p_series_id: input.seriesId }),
        p_description: input.description.trim(),
        ...(input.artworkPath === null
          ? {}
          : { p_artwork_path: input.artworkPath, p_artwork_alt: input.artworkAlt ?? "" }),
        ...(input.startsOn === null ? {} : { p_starts_on: input.startsOn }),
        ...(input.endsOn === null ? {} : { p_ends_on: input.endsOn }),
      });
      failed(error);
      return data as string;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteSeries() {
  const supabase = useSupabase();
  const invalidate = useSermonInvalidation();
  return useMutation({
    mutationFn: async (seriesId: string) => {
      const { error } = await supabase.rpc("delete_sermon_series", { p_series_id: seriesId });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

export function useSaveSpeaker() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useSermonInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly speakerId: string | null;
      readonly name: string;
      readonly roleTitle: string;
      readonly isActive: boolean;
    }): Promise<string> => {
      if (tenantId === null) throw new Error("no church resolved");
      const { data, error } = await supabase.rpc("save_sermon_speaker", {
        p_tenant_id: tenantId,
        p_name: input.name.trim(),
        ...(input.speakerId === null ? {} : { p_speaker_id: input.speakerId }),
        p_role_title: input.roleTitle.trim(),
        p_is_active: input.isActive,
      });
      failed(error);
      return data as string;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteSpeaker() {
  const supabase = useSupabase();
  const invalidate = useSermonInvalidation();
  return useMutation({
    mutationFn: async (speakerId: string) => {
      const { error } = await supabase.rpc("delete_sermon_speaker", { p_speaker_id: speakerId });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

export function useSaveSermonSettings() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useSermonInvalidation();
  return useMutation({
    mutationFn: async (settings: SermonSettingsRow) => {
      if (tenantId === null) throw new Error("no church resolved");
      const { error } = await supabase.rpc("save_sermon_settings", {
        p_tenant_id: tenantId,
        p_fields: {
          enabled: settings.enabled,
          title: settings.title.trim(),
          description: settings.description.trim(),
          author: settings.author.trim(),
          email: settings.email.trim(),
          artworkPath: settings.artworkPath ?? "",
          category: settings.category,
          explicit: settings.explicit,
        },
      });
      failed(error);
    },
    onSuccess: invalidate,
  });
}
